import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '../../services/adminService';

const initialState = {
  pendingApprovals: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  filters: {
    page: 1,
    limit: 10,
    sortOrder: 'asc',
    role: 'player',
    sortBy: 'firstName',
  },
  loading: false,
  error: null,
  approvalLoading: {},
  // Dashboard summary
  summary: {
    totalUsers: 0,
    totalPlayers: 0,
    totalCoaches: 0,
    totalScouts: 0,
    totalClaimedProfiles: 0,
    pendingApprovals: 0,
  },
  // Manual Edits state
  usersByRole: [],
  manualEditsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasMore: false,
  },
  manualEditsFilters: {
    page: 1,
    limit: 20,
    role: 'player',
    search: '',
  },
  manualEditsLoading: false,
  manualEditsError: null,
};

export const fetchPendingApprovals = createAsyncThunk(
  'admin/fetchPendingApprovals',
  async (params, { rejectWithValue }) => {
    try {
      const data = await adminService.getPendingApprovals(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch pending approvals'
      );
    }
  }
);

export const approveUser = createAsyncThunk(
  'admin/approveUser',
  async (id, { rejectWithValue }) => {
    try {
      const data = await adminService.approveUser(id);
      return { id, data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to approve user'
      );
    }
  }
);

export const rejectUser = createAsyncThunk(
  'admin/rejectUser',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const data = await adminService.rejectUser(id, reason);
      return { id, data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to reject user'
      );
    }
  }
);

export const fetchUsersByRoles = createAsyncThunk(
  'admin/fetchUsersByRoles',
  async (params, { rejectWithValue }) => {
    try {
      const data = await adminService.getUsersByRoles(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch users by roles'
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'admin/updateUserProfile',
  async ({ id, profileData }, { rejectWithValue }) => {
    try {
      const data = await adminService.updateUserProfile(id, profileData);
      return { id, data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update user profile'
      );
    }
  }
);

export const fetchManualEditsUsers = createAsyncThunk(
  'admin/fetchManualEditsUsers',
  async (params, { rejectWithValue }) => {
    try {
      const data = await adminService.getManualEditsUsers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch manual edits users'
      );
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setManualEditsFilters: (state, action) => {
      state.manualEditsFilters = { ...state.manualEditsFilters, ...action.payload };
    },
    clearManualEditsError: (state) => {
      state.manualEditsError = null;
    },
    resetManualEditsFilters: (state) => {
      state.manualEditsFilters = initialState.manualEditsFilters;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.loading = false;
        const newApprovals = action.payload.pendingApprovals || [];
        const currentPage = action.payload.pagination?.currentPage || 1;

        // If it's page 1, replace the data. Otherwise, append it for "Load More" functionality.
        if (currentPage === 1) {
          state.pendingApprovals = newApprovals;
        } else {
          state.pendingApprovals = [...state.pendingApprovals, ...newApprovals];
        }

        state.pagination = action.payload.pagination || initialState.pagination;
        // Store summary data if available
        if (action.payload.summary) {
          state.summary = {
            totalUsers: action.payload.summary.totalUsers || 0,
            totalPlayers: action.payload.summary.totalPlayers || 0,
            totalCoaches: action.payload.summary.totalCoaches || 0,
            totalScouts: action.payload.summary.totalScouts || 0,
            totalClaimedProfiles: action.payload.summary.totalClaimedProfiles || 0,
            pendingApprovals: action.payload.summary.pendingApprovals || 0,
          };
        }
        state.error = null;
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveUser.pending, (state, action) => {
        state.approvalLoading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(approveUser.fulfilled, (state, action) => {
        state.approvalLoading[action.payload.id] = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (user) => user._id !== action.payload.id
        );
        state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
      })
      .addCase(approveUser.rejected, (state, action) => {
        state.approvalLoading[action.meta.arg] = false;
        state.error = action.payload;
      })
      .addCase(rejectUser.pending, (state, action) => {
        state.approvalLoading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(rejectUser.fulfilled, (state, action) => {
        state.approvalLoading[action.payload.id] = false;
        state.pendingApprovals = state.pendingApprovals.filter(
          (user) => user._id !== action.payload.id
        );
        state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
      })
      .addCase(rejectUser.rejected, (state, action) => {
        state.approvalLoading[action.meta.arg] = false;
        state.error = action.payload;
      })
      .addCase(fetchUsersByRoles.pending, (state) => {
        state.manualEditsLoading = true;
        state.manualEditsError = null;
      })
      .addCase(fetchUsersByRoles.fulfilled, (state, action) => {
        state.manualEditsLoading = false;
        const newUsers = action.payload.users || [];
        const currentPage = action.payload.pagination?.currentPage || 1;

        // If it's page 1, replace the data. Otherwise, append it.
        if (currentPage === 1) {
          state.usersByRole = newUsers;
        } else {
          state.usersByRole = [...state.usersByRole, ...newUsers];
        }

        state.manualEditsPagination = action.payload.pagination || initialState.manualEditsPagination;
        state.manualEditsError = null;
      })
      .addCase(fetchUsersByRoles.rejected, (state, action) => {
        state.manualEditsLoading = false;
        state.manualEditsError = action.payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.manualEditsLoading = true;
        state.manualEditsError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.manualEditsLoading = false;
        // Update the user in the local list
        const updatedUser = action.payload.data?.user;
        if (updatedUser) {
          state.usersByRole = state.usersByRole.map((user) =>
            user._id === action.payload.id ? { ...user, ...updatedUser } : user
          );
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.manualEditsLoading = false;
        state.manualEditsError = action.payload;
      })
      .addCase(fetchManualEditsUsers.pending, (state) => {
        state.manualEditsLoading = true;
        state.manualEditsError = null;
      })
      .addCase(fetchManualEditsUsers.fulfilled, (state, action) => {
        state.manualEditsLoading = false;
        const newUsers = action.payload.users || [];
        const currentPage = action.payload.pagination?.currentPage || 1;

        // If it's page 1, replace the data. Otherwise, append it.
        if (currentPage === 1) {
          state.usersByRole = newUsers;
        } else {
          state.usersByRole = [...state.usersByRole, ...newUsers];
        }

        state.manualEditsPagination = action.payload.pagination || initialState.manualEditsPagination;
        state.manualEditsError = null;
      })
      .addCase(fetchManualEditsUsers.rejected, (state, action) => {
        state.manualEditsLoading = false;
        state.manualEditsError = action.payload;
      });
  },
});

export const {
  setFilters,
  clearError,
  resetFilters,
  setManualEditsFilters,
  clearManualEditsError,
  resetManualEditsFilters,
} = adminSlice.actions;
export default adminSlice.reducer;
